import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHeader } from '@/components/ui/Card';
import { ToggleRow } from '@/components/ui/ToggleRow';
import { Alert } from '@/components/ui/Alert';
import { SaveBar } from '@/components/ui/SaveBar';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToggleModule } from '@/hooks/useToggleModule';
import type { GuildConfigModule } from '@/lib/api-client/guild-config';

interface Feature {
  key: string;
  label: ReactNode;
  description?: ReactNode;
}

interface ModuleTogglePanelProps {
  module: GuildConfigModule;
  title: string;
  cardDescription?: string;
  /** Omit for a single master on/off switch (most modules); provide for
   * Security/AutoMod's real per-feature keys — MUST match the keys
   * bridge/config_registry.py's cog actually returns, or the PATCH is
   * rejected server-side (never silently ignored). */
  features?: Feature[];
  /** Label for the single master switch when `features` is omitted. */
  masterLabel?: string;
  /** Shown once permission is confirmed granted, above the toggles. */
  permissionNote?: string;
  onReady?: (states: Record<string, boolean>) => void;
}

/**
 * Shared UI for every "dashboard-toggle" module (see useToggleModule's
 * docstring) — one component instead of 11 near-identical pages. A page
 * using this still owns its own surrounding layout/cards; this is just the
 * "load → toggle → save/reset" card, matching the AI page's own states
 * (loading, forbidden, unavailable, ready) and SaveBar usage exactly.
 */
export function ModuleTogglePanel({
  module,
  title,
  cardDescription,
  features,
  masterLabel = `Enable ${title}`,
  permissionNote,
}: ModuleTogglePanelProps) {
  const { guildId } = useParams<{ guildId: string }>();
  const { state, current, dirty, saving, saveError, resetting, setFeature, setAll, save, discard, reset } =
    useToggleModule(guildId, module);

  if (state.kind === 'loading') {
    return (
      <Card>
        <CardHeader title={title} description={cardDescription} />
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </Card>
    );
  }

  if (state.kind === 'forbidden') {
    return (
      <Card>
        <CardHeader title={title} description={cardDescription} />
        <Alert tone="danger" title="You don't have permission to view this" description="Your current Discord role in this server doesn't meet this feature's permission requirement." />
      </Card>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <Card>
        <CardHeader title={title} description={cardDescription} />
        <Alert tone="danger" title="Couldn't load this setting" description={state.message} />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title={title} description={cardDescription ?? permissionNote} />
      {saveError && (
        <div className="mb-3">
          <Alert tone="danger" title="Couldn't save" description={saveError} />
        </div>
      )}
      <div className="divide-y divide-border-subtle">
        {features ? (
          features.map((f) => (
            <ToggleRow
              key={f.key}
              title={f.label}
              description={f.description}
              checked={current[f.key] ?? false}
              onChange={(v) => setFeature(f.key, v)}
            />
          ))
        ) : (
          <ToggleRow
            title={masterLabel}
            checked={current._master ?? false}
            onChange={(v) => setAll(v)}
          />
        )}
      </div>
      <SaveBar visible={dirty || saving} onSave={save} onDiscard={discard} saving={saving} />
      {!dirty && (
        <button
          type="button"
          onClick={reset}
          disabled={resetting}
          className="mt-3 text-small text-text-tertiary hover:text-text-secondary underline underline-offset-2 disabled:opacity-50"
        >
          {resetting ? 'Resetting…' : 'Reset to defaults'}
        </button>
      )}
    </Card>
  );
}
